import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles, ArrowLeft, ArrowRight } from 'lucide-react';
import IframeOverlay from '../components/IframeOverlay';
import { Seo } from '../components/Seo';
import { config } from '../config';
import type { PropertyData } from '../types';

export function DemoViewPage() {
  const { slug } = useParams<{ slug: string }>();
  const [propertyData, setPropertyData] = useState<PropertyData | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    setLoading(true);
    setFetchError(null);
    setNotFound(false);

    (async () => {
      try {
        const res = await fetch(`${config.apiUrl}/v1/demo/${slug}`);
        if (cancelled) return;
        if (res.status === 404) {
          setNotFound(true);
          return;
        }
        if (!res.ok) {
          setFetchError(`Server error (${res.status}). Please try again.`);
          return;
        }
        const data: PropertyData = await res.json();
        if (!cancelled) setPropertyData(data);
      } catch {
        if (!cancelled) setFetchError('Failed to connect to server. Please try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [slug, retryCount]);

  if (fetchError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Something went wrong</h2>
          <p className="text-gray-600 mb-6">{fetchError}</p>
          <button
            onClick={() => setRetryCount((c) => c + 1)}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full text-center bg-white/80 backdrop-blur-xl rounded-3xl border border-white/60 p-10 shadow-large"
        >
          <div className="w-16 h-16 bg-gradient-to-br from-lasoxp-500 to-accent-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-glow">
            <Sparkles className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">
            Demo Not Available
          </h2>
          <p className="text-gray-600 mb-6">
            This property hasn't been set up for a demo yet.
            Want to see LasoXP in action on your website?
          </p>
          <div className="space-y-3">
            <a
              href={`${config.signupUrl}?property=${slug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full px-6 py-3 bg-gradient-to-r from-lasoxp-600 to-accent-600 text-white rounded-xl hover:from-lasoxp-700 hover:to-accent-700 transition-colors font-medium flex items-center justify-center gap-2"
            >
              Request a Demo
              <ArrowRight className="w-4 h-4" />
            </a>
            <Link
              to="/"
              className="block w-full px-6 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors font-medium flex items-center justify-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              View Example Hotels
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <div className="w-16 h-16 bg-gradient-to-br from-lasoxp-500 to-accent-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-glow">
            <Sparkles className="w-8 h-8 text-white" />
          </div>
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-lasoxp-600 mx-auto mt-4" />
        </motion.div>
      </div>
    );
  }

  // Derive suggestions from KB categories
  const suggestions =
    propertyData?.knowledgeItems && propertyData.knowledgeItems.length > 0
      ? propertyData.knowledgeItems
          .slice(0, 4)
          .map((item) => `Tell me about ${item.name}`)
      : [
          'What amenities do you offer?',
          'Tell me about dining options',
          'What activities are available?',
          'Room information',
        ];

  const propertyUrl =
    propertyData?.website || `https://www.${slug}.com`;

  const propertyName = propertyData?.name ?? slug ?? 'Demo';
  const seoTitle = `${propertyName} · LasoXP Demo`;
  const seoDescription = propertyData?.name
    ? `Try the LasoXP AI concierge live on ${propertyData.name}. Ask it anything about the property — rooms, dining, amenities, and more.`
    : 'Try the LasoXP AI concierge live on a real hotel website.';

  return (
    <div className="demo-container relative">
      <Seo
        title={seoTitle}
        description={seoDescription}
        canonical={slug ? `/${slug}` : undefined}
      />
      <IframeOverlay
        url={propertyUrl}
        propertyData={propertyData}
        suggestions={suggestions}
      />
    </div>
  );
}
